/**
 * redline-compare — before/after slider web component
 * Auto-animates between BEFORE (origin) and AFTER (kit) with requestAnimationFrame.
 * Pauses on drag/touch, resumes after 2.6 s of inactivity.
 * Respects prefers-reduced-motion.
 */
class RedlineCompare extends HTMLElement {
  constructor() {
    super();
    this._pos = 0.1; // start on BEFORE side (10% revealed = mostly origin)
    this._target = 0.9;
    this._dragging = false;
    this._idleTimer = null;
    this._raf = null;
    this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    this._beforeImg = this.querySelector('[data-before]');
    this._afterImg = this.querySelector('[data-after]');
    this._handle = this.querySelector('[data-handle]');

    if (!this._beforeImg || !this._afterImg) return;

    this._applyClip(this._pos);

    if (!this._reduced) {
      this._startAnim();
    } else {
      this._applyClip(0.5);
    }

    this._bindEvents();
  }

  disconnectedCallback() {
    this._stopAnim();
    this._clearIdle();
    this._removeEvents();
  }

  _applyClip(pct) {
    const p = Math.max(0, Math.min(1, pct));
    this._pos = p;
    const xPct = (p * 100).toFixed(2) + '%';
    this._beforeImg.style.clipPath = `inset(0 ${(100 - p * 100).toFixed(2)}% 0 0)`;
    this._afterImg.style.clipPath = `inset(0 0 0 ${(p * 100).toFixed(2)}%)`;
    if (this._handle) {
      this._handle.style.left = xPct;
    }
  }

  _startAnim() {
    this._stopAnim();
    let dir = 1;
    let lastTime = null;

    const tick = (ts) => {
      if (!lastTime) lastTime = ts;
      const dt = (ts - lastTime) / 1000;
      lastTime = ts;

      if (!this._dragging) {
        const speed = 0.18; // fraction per second
        this._target += dir * speed * dt;
        if (this._target >= 0.9) { this._target = 0.9; dir = -1; }
        if (this._target <= 0.1) { this._target = 0.1; dir = 1; }
        this._pos += (this._target - this._pos) * Math.min(1, dt * 4);
        this._applyClip(this._pos);
      }

      this._raf = requestAnimationFrame(tick);
    };

    this._raf = requestAnimationFrame(tick);
  }

  _stopAnim() {
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
  }

  _clearIdle() {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
  }

  _onDragStart(x) {
    this._dragging = true;
    this._clearIdle();
    this._setFromX(x);
  }

  _onDragMove(x) {
    if (!this._dragging) return;
    this._setFromX(x);
  }

  _onDragEnd() {
    this._dragging = false;
    this._idleTimer = setTimeout(() => {
      this._target = this._pos;
    }, 2600);
  }

  _setFromX(clientX) {
    const rect = this.getBoundingClientRect();
    const pct = (clientX - rect.left) / rect.width;
    this._target = pct;
    this._applyClip(pct);
  }

  _bindEvents() {
    this._onMouseDown = (e) => { e.preventDefault(); this._onDragStart(e.clientX); };
    this._onMouseMove = (e) => { this._onDragMove(e.clientX); };
    this._onMouseUp   = () => { this._onDragEnd(); };

    this._onTouchStart = (e) => { this._onDragStart(e.touches[0].clientX); };
    this._onTouchMove  = (e) => { e.preventDefault(); this._onDragMove(e.touches[0].clientX); };
    this._onTouchEnd   = () => { this._onDragEnd(); };

    this.addEventListener('mousedown',  this._onMouseDown);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup',   this._onMouseUp);

    this.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this.addEventListener('touchmove',  this._onTouchMove,  { passive: false });
    this.addEventListener('touchend',   this._onTouchEnd);
  }

  _removeEvents() {
    this.removeEventListener('mousedown',  this._onMouseDown);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mouseup',   this._onMouseUp);
    this.removeEventListener('touchstart', this._onTouchStart);
    this.removeEventListener('touchmove',  this._onTouchMove);
    this.removeEventListener('touchend',   this._onTouchEnd);
  }
}

customElements.define('redline-compare', RedlineCompare);
